import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Filter, Download, CreditCard as Edit, Trash2, Phone, Mail, Building2, User, Calendar, Star, ArrowRight, Eye, UserCheck, Brain, X, RotateCcw, AlertCircle, Archive } from 'lucide-react';
import type { Lead } from '../types';
import { LEAD_STATUS, LEAD_STATUS_COLORS, LEAD_STATUS_LABELS } from '../constants/leadStatuses';
import { leadsService, activitiesService } from '../services/supabaseService';
import LeadModal from './LeadModal';
import ConvertLeadModal from './ConvertLeadModal';
import ArchiveLeadModal from './ArchiveLeadModal';
import LeadAIAnalysisPanel from './LeadAIAnalysisPanel';
import LeadSourceBadge from './LeadSourceBadge';
import LeadQualifiedBadge from './LeadQualifiedBadge';
import { useNotifications } from '../hooks/useNotifications';
import { aiService } from '../services/aiService';
import { useDataSync, useDataSubscription } from '../contexts/DataSyncContext';
import { useDataLoader } from '../hooks/useDataLoader';
import { useToast } from '../hooks/useToast';
import { parseLeadFormData, getLeadRealSource } from '../utils/leadFormDataParser';
import authService from '../services/authService';

const LeadsList: React.FC = () => {
  const { notifyNewLead, notifyLeadConverted } = useNotifications();
  const { notifyDataChange } = useDataSync();
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [convertingLead, setConvertingLead] = useState<Lead | null>(null);
  const [leadDisposition, setLeadDisposition] = useState<{ lead: Lead; action: 'archive' | 'delete' } | null>(null);
  const [selectedLeadForAI, setSelectedLeadForAI] = useState<Lead | null>(null);
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    source: '',
    owner: '',
    productInterest: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const sourceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { toast, showToast, clearToast } = useToast();
  const [confirmAction, setConfirmAction] = useState<{ message: string; onConfirm: () => void } | null>(null);
  const [savedSourceLeadId, setSavedSourceLeadId] = useState<string | null>(null);
  const [leadsStats, setLeadsStats] = useState<Record<string, number>>({
    [LEAD_STATUS.NUEVO]: 0,
    [LEAD_STATUS.CONTACTADO]: 0,
    [LEAD_STATUS.EN_SEGUIMIENTO]: 0,
    [LEAD_STATUS.CALIFICADO]: 0,
    [LEAD_STATUS.PROPUESTA]: 0,
    [LEAD_STATUS.GANADO]: 0,
    [LEAD_STATUS.PERDIDO]: 0,
    [LEAD_STATUS.CONVERTIDO]: 0,
    [LEAD_STATUS.DESCARTADO]: 0
  });

  const { data: leads, loading, isRevalidating, refetch, invalidate } = useDataLoader<Lead[]>(
    'leads-list',
    () => {
      if (filters.status === LEAD_STATUS.CONVERTIDO) {
        return leadsService.getAllConverted();
      }
      return leadsService.getAll();
    },
    {
      ttl: 30,
      onSuccess: (data) => {
        if (import.meta.env.DEV) console.log(`✅ LeadsList: ${data.length} leads cargados`);
      }
    }
  );

  const debouncedRefetch = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (import.meta.env.DEV) console.log('🔄 LeadsList: Debounced refetch triggered');
      refetch();
    }, 500);
  }, [refetch]);

  useDataSubscription('leads', (event) => {
    if (import.meta.env.DEV) console.log('🔄 LeadsList: Recibido evento de sincronización:', event);
    debouncedRefetch();
  });

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (leads) {
      applyFilters(searchTerm, filters);
    }
  }, [leads, searchTerm, filters]);

  useEffect(() => {
    // Derivar los stats del array de leads que ya tenemos en memoria en vez
    // de hacer un round-trip adicional a Supabase por cada refresh. Antes
    // leadsService.getStats() traía TODAS las filas con select('status')
    // — no escalaba a partir de unos miles de leads.
    if (!leads) return;
    const stats: Record<string, number> = {};
    for (const lead of leads) {
      stats[lead.status] = (stats[lead.status] || 0) + 1;
    }
    setLeadsStats(prev => ({ ...prev, ...stats }));
  }, [leads]);

  const statusColors = LEAD_STATUS_COLORS;
  const statusLabels = LEAD_STATUS_LABELS;

  const sourceColors = {
    form_web: 'bg-blue-100 text-blue-800',
    phone_call: 'bg-green-100 text-green-800',
    email: 'bg-purple-100 text-purple-800',
    referral: 'bg-orange-100 text-orange-800',
    manual: 'bg-gray-100 text-gray-800',
    social_media: 'bg-pink-100 text-pink-800',
    advertising: 'bg-indigo-100 text-indigo-800',
    web_form_contacto: 'bg-cyan-100 text-cyan-800',
    elevenlabs_web: 'bg-teal-100 text-teal-800',
    elevenlabs_whatsapp: 'bg-emerald-100 text-emerald-800',
    public_agenda: 'bg-sky-100 text-sky-800',
    shakers: 'bg-amber-100 text-amber-800',
    sortlist: 'bg-lime-100 text-lime-800',
    other: 'bg-gray-100 text-gray-800'
  };

  const sourceLabels = {
    form_web: 'Formulario Web',
    phone_call: 'Llamada',
    email: 'Email',
    referral: 'Referido',
    manual: 'Manual',
    social_media: 'Redes Sociales',
    advertising: 'Publicidad',
    web_form_contacto: 'Web: Contacto',
    elevenlabs_web: 'Web: ElevenLabs',
    elevenlabs_whatsapp: 'WhatsApp: ElevenLabs',
    public_agenda: 'Agenda Pública',
    shakers: 'Shakers',
    sortlist: 'Sortlist',
    other: 'Otro'
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    applyFilters(term, filters);
  };

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    applyFilters(searchTerm, newFilters);

    if (newFilters.status === LEAD_STATUS.CONVERTIDO || (filters.status === LEAD_STATUS.CONVERTIDO && newFilters.status !== LEAD_STATUS.CONVERTIDO)) {
      invalidate();
      refetch();
    }
  };

  const applyFilters = useCallback((search: string, currentFilters: typeof filters) => {
    if (!leads) {
      setFilteredLeads([]);
      return;
    }

    let filtered = leads.filter(lead => {
      const matchesSearch = !search ||
        lead.fullName.toLowerCase().includes(search.toLowerCase()) ||
        lead.email.toLowerCase().includes(search.toLowerCase()) ||
        lead.company.toLowerCase().includes(search.toLowerCase()) ||
        lead.phone?.includes(search);

      const matchesStatus = !currentFilters.status || lead.status === currentFilters.status;

      const realSource = getLeadRealSource(lead);
      const matchesSource = !currentFilters.source || lead.source === currentFilters.source || realSource === currentFilters.source;

      const matchesOwner = !currentFilters.owner || lead.owner === currentFilters.owner;
      const matchesProduct = !currentFilters.productInterest || lead.productInterest === currentFilters.productInterest;

      return matchesSearch && matchesStatus && matchesSource && matchesOwner && matchesProduct;
    });

    setFilteredLeads(filtered);
  }, [leads]);

  const handleSaveLead = async (leadData: Partial<Lead>) => {
    try {
      if (editingLead) {
        // Update existing lead
        if (import.meta.env.DEV) console.log('🔄 Actualizando lead existente:', editingLead.id, leadData);
        await leadsService.update(editingLead.id, leadData);

        invalidate();
        await refetch();

        notifyDataChange('leads', 'update', editingLead.id);

        showToast('Lead actualizado correctamente');
      } else {
        // Create new lead
        const newLead = await leadsService.create({
          fullName: leadData.fullName || '',
          email: leadData.email || '',
          phone: leadData.phone,
          company: leadData.company || '',
          role: leadData.role,
          source: leadData.source || 'manual',
          status: leadData.status || LEAD_STATUS.NUEVO,
          productInterest: leadData.productInterest || '',
          owner: leadData.owner || authService.getCurrentSession()?.user?.name || '',
          notes: leadData.notes || '',
          formData: leadData.formData || {},
          score: leadData.score || 0,
          lastContactDate: leadData.lastContactDate,
          nextFollowUp: leadData.nextFollowUp
        });

        // Create automatic activity for ElevenLabs leads
        const metadata = parseLeadFormData(newLead);
        if (metadata.realSource === 'elevenlabs_web' || metadata.realSource === 'elevenlabs_whatsapp') {
          try {
            const channelName = metadata.realSource === 'elevenlabs_web' ? 'Web' : 'WhatsApp';

            let activityDescription = `Lead generado automáticamente por agente ElevenLabs (${channelName}).\n\n`;

            if (metadata.projectType) {
              activityDescription += `Tipo de proyecto: ${metadata.projectType}\n`;
            }

            if (metadata.projectDescription) {
              activityDescription += `\nDescripción: ${metadata.projectDescription}\n`;
            }

            if (metadata.budgetRange) {
              activityDescription += `\nPresupuesto estimado: ${metadata.budgetRange}\n`;
            }

            if (metadata.estimatedTimeframe) {
              activityDescription += `\nPlazo estimado: ${metadata.estimatedTimeframe}\n`;
            }

            if (metadata.isQualified !== null) {
              activityDescription += `\nLead qualified: ${metadata.isQualified ? 'Sí' : 'No'}\n`;
            }

            await activitiesService.create({
              type: 'nota_interna',
              datetime: new Date(),
              description: activityDescription,
              owner: leadData.owner || authService.getCurrentSession()?.user?.name || '',
              leadId: newLead.id
            });

            if (import.meta.env.DEV) console.log('✅ Actividad automática creada para lead de ElevenLabs');
          } catch (activityError) {
            console.error('❌ Error al crear actividad automática:', activityError);
          }
        }
        
        // Analyze lead with AI to get priority score
        try {
          if (import.meta.env.DEV) console.log('🤖 Analizando nuevo lead con IA...');
          const aiInsight = await aiService.analyzeFormSubmission(
            { message: newLead.notes, productInterest: newLead.productInterest },
            newLead
          );
          
          // Convert AI priority to numeric score
          let aiScore = newLead.score;
          if (aiInsight.data?.priority === 'high') {
            aiScore = 90;
          } else if (aiInsight.data?.priority === 'medium') {
            aiScore = 60;
          } else if (aiInsight.data?.priority === 'low') {
            aiScore = 30;
          }
          
          // Update lead with AI score
          if (aiScore !== newLead.score) {
            if (import.meta.env.DEV) console.log(`🎯 Actualizando puntuación IA: ${newLead.score} → ${aiScore}`);
            await leadsService.update(newLead.id, { score: aiScore });
            newLead.score = aiScore;
          }
          
          if (import.meta.env.DEV) console.log('✅ Análisis IA completado:', aiInsight.data?.priority, `(${aiScore} puntos)`);
        } catch (aiError) {
          console.warn('⚠️ Error en análisis IA (continuando):', aiError);
        }

        invalidate();
        await refetch();

        notifyDataChange('leads', 'create', newLead.id);

        await notifyNewLead(
          `${newLead.fullName} - ${newLead.company}`,
          undefined
        );
        
      }
      setIsModalOpen(false);
      setEditingLead(null);
    } catch (error) {
      console.error('Error saving lead:', error);
      showToast('Error al guardar el lead', 'error');
    }
  };

  // Handle inline source change
  const handleSourceChange = async (leadId: string, newSource: Lead['source']) => {
    try {
      if (import.meta.env.DEV) console.log('🔄 Guardando cambio de fuente automáticamente:', leadId, 'a:', newSource);

      await leadsService.update(leadId, { source: newSource });

      invalidate();
      await refetch();

      notifyDataChange('leads', 'update', leadId);

      if (sourceTimerRef.current) clearTimeout(sourceTimerRef.current);
      setSavedSourceLeadId(leadId);
      sourceTimerRef.current = setTimeout(() => setSavedSourceLeadId(null), 1500);
    } catch (error) {
      console.error('Error updating lead source:', error);
      invalidate();
      await refetch();
      showToast('Error al guardar la fuente del lead', 'error');
    }
  };

  const handleConvertLead = (lead: Lead) => {
    setConvertingLead(lead);
    setIsConvertModalOpen(true);
  };

  const handleSaveConversion = async (contactData: any) => {
    if (!convertingLead) return;
    
    try {
      await leadsService.convertToContact(convertingLead.id, contactData);

      invalidate();
      await refetch();

      notifyDataChange('leads', 'update', convertingLead.id);
      notifyDataChange('contacts', 'create');

      await notifyLeadConverted(
        convertingLead.fullName,
        convertingLead.company,
        undefined
      );

      setIsConvertModalOpen(false);
      setConvertingLead(null);
      showToast('Lead convertido a cliente exitosamente');
    } catch (error) {
      console.error('Error converting lead:', error);
      showToast('Error al convertir el lead', 'error');
    }
  };

  const handleDispositionConfirm = async (reason: string, note: string) => {
    if (!leadDisposition) return;
    const { lead, action } = leadDisposition;
    const deleting = action === 'delete';

    try {
      await leadsService.recordDisposition(lead.id, action, reason, note);
    } catch (error) {
      console.error('Error al guardar el descarte del lead:', error);
      showToast(deleting ? 'Error al eliminar el lead' : 'Error al archivar el lead', 'error');
      throw error;
    }

    invalidate();
    notifyDataChange('leads', deleting ? 'delete' : 'update', lead.id);
    void refetch();
    showToast(deleting ? 'Lead eliminado correctamente' : 'Lead archivado correctamente');
  };

  const exportToCSV = () => {
    const headers = ['Nombre', 'Email', 'Teléfono', 'Empresa', 'Fuente', 'Estado', 'Producto de Interés', 'Propietario', 'Puntuación'];
    const csvContent = [
      headers.join(','),
      ...filteredLeads.map(lead => [
        lead.fullName,
        lead.email,
        lead.phone || '',
        lead.company,
        sourceLabels[lead.source],
        statusLabels[lead.status],
        lead.productInterest,
        lead.owner,
        lead.score.toString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleRestoreLead = (leadId: string, newStatus: Lead['status']) => {
    setConfirmAction({
      message: `¿Estás seguro de que quieres restaurar este lead a estado: ${statusLabels[newStatus]}?`,
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await leadsService.update(leadId, { status: newStatus });
          invalidate();
          await refetch();
          notifyDataChange('leads', 'update', leadId);
          showToast(`Lead restaurado a estado: ${statusLabels[newStatus]}`);
        } catch (error) {
          console.error('Error restoring lead:', error);
          showToast('Error al restaurar el lead', 'error');
        }
      }
    });
  };

  const handleShowAI = (lead: Lead) => {
    setSelectedLeadForAI(lead);
    setShowAIPanel(true);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    if (score >= 40) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3">Cargando leads...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-4 p-4 h-full min-w-0">
      {/* Toast notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-[100] flex items-center space-x-2 px-4 py-3 rounded-lg shadow-lg text-white text-sm transition-all ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          <span>{toast.message}</span>
          <button onClick={clearToast} className="ml-2 opacity-75 hover:opacity-100">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Confirm dialog */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4 p-6">
            <p className="text-gray-800 text-sm mb-5">{confirmAction.message}</p>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setConfirmAction(null)} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                Cancelar
              </button>
              <button onClick={confirmAction.onConfirm} className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors">
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={`transition-all duration-300 overflow-y-auto flex-1 min-h-0 min-w-0 ${showAIPanel ? 'lg:w-2/3' : 'w-full'}`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex-1">
          <div className="flex items-center space-x-3">
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Leads</h1>
            {isRevalidating && (
              <div className="flex items-center space-x-2 text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                <span className="text-xs">Actualizando...</span>
              </div>
            )}
          </div>
          <p className="text-sm lg:text-base text-gray-600">Gestiona leads potenciales antes de convertirlos en clientes</p>
        </div>
        <div className="flex space-x-2 lg:space-x-3">
          <button
            onClick={exportToCSV}
            className="flex items-center space-x-2 px-3 lg:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Exportar CSV</span>
            <span className="sm:hidden">CSV</span>
          </button>
          <button
            onClick={() => {
              setEditingLead(null);
              setIsModalOpen(true);
            }}
            className="flex items-center space-x-2 px-3 lg:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Nuevo Lead</span>
            <span className="sm:hidden">Nuevo</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4 mb-6">
        {[
          { status: LEAD_STATUS.NUEVO, label: 'Nuevos', count: leadsStats[LEAD_STATUS.NUEVO], color: 'bg-blue-50 text-blue-600' },
          { status: LEAD_STATUS.CONTACTADO, label: 'Contactados', count: leadsStats[LEAD_STATUS.CONTACTADO], color: 'bg-yellow-50 text-yellow-600' },
          { status: LEAD_STATUS.CALIFICADO, label: 'Calificados', count: leadsStats[LEAD_STATUS.CALIFICADO], color: 'bg-green-50 text-green-600' },
          { status: LEAD_STATUS.CONVERTIDO, label: 'Convertidos', count: leadsStats[LEAD_STATUS.CONVERTIDO], color: 'bg-emerald-50 text-emerald-600' },
          { status: LEAD_STATUS.DESCARTADO, label: 'Descartados', count: leadsStats[LEAD_STATUS.DESCARTADO], color: 'bg-red-50 text-red-600' }
        ].map((stat) => (
          <div key={stat.status} className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 lg:p-4">
            <div className={`text-center p-3 lg:p-4 rounded-lg ${stat.color}`}>
              <div className="text-xl lg:text-2xl font-bold mb-1">{stat.count}</div>
              <p className="text-xs lg:text-sm font-medium">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Filters for Web Leads */}
      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg shadow-sm border border-cyan-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center space-x-3">
            <div className="bg-cyan-100 p-2 rounded-lg">
              <Filter className="w-5 h-5 text-cyan-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Filtros Rápidos - Canales Digitales</h3>
              <p className="text-xs text-gray-600 hidden md:block">Filtra leads por fuente de entrada: web, agentes IA, agenda pública</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => handleFilterChange({ ...filters, source: 'web_form_contacto' })}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filters.source === 'web_form_contacto'
                  ? 'bg-cyan-600 text-white'
                  : 'bg-white text-cyan-700 hover:bg-cyan-50 border border-cyan-200'
              }`}
            >
              Web: Contacto
            </button>
            <button
              onClick={() => handleFilterChange({ ...filters, source: 'elevenlabs_web' })}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filters.source === 'elevenlabs_web'
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-teal-700 hover:bg-teal-50 border border-teal-200'
              }`}
            >
              ElevenLabs Web
            </button>
            <button
              onClick={() => handleFilterChange({ ...filters, source: 'elevenlabs_whatsapp' })}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filters.source === 'elevenlabs_whatsapp'
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-green-700 hover:bg-green-50 border border-green-200'
              }`}
            >
              ElevenLabs WhatsApp
            </button>
            <button
              onClick={() => handleFilterChange({ ...filters, source: 'public_agenda' })}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filters.source === 'public_agenda'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-purple-700 hover:bg-purple-50 border border-purple-200'
              }`}
            >
              Agenda Pública
            </button>
            {filters.source && (
              <button
                onClick={() => handleFilterChange({ ...filters, source: '' })}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors flex items-center space-x-1"
              >
                <X className="w-3 h-3" />
                <span>Limpiar</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <div>
            <input
              type="text"
              placeholder="Buscar leads..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange({ ...filters, status: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Todos los estados</option>
            {Object.entries(LEAD_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <select
            value={filters.source}
            onChange={(e) => handleFilterChange({ ...filters, source: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Todas las fuentes</option>
            <optgroup label="Fuentes Web">
              <option value="web_form_contacto">Web: Contacto</option>
              <option value="elevenlabs_web">Web: ElevenLabs</option>
              <option value="public_agenda">Agenda Pública</option>
              <option value="form_web">Formulario Web (Legacy)</option>
            </optgroup>
            <optgroup label="Otras Fuentes">
              <option value="phone_call">Llamada</option>
              <option value="email">Email</option>
              <option value="referral">Referido</option>
              <option value="manual">Manual</option>
              <option value="social_media">Redes Sociales</option>
              <option value="advertising">Publicidad</option>
              <option value="shakers">Shakers</option>
              <option value="sortlist">Sortlist</option>
            </optgroup>
          </select>
          <select
            value={filters.owner}
            onChange={(e) => handleFilterChange({ ...filters, owner: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Todos los propietarios</option>
            <option value="Ana García">Ana García</option>
            <option value="Carlos López">Carlos López</option>
            <option value="María Rodríguez">María Rodríguez</option>
          </select>
          <select
            value={filters.productInterest}
            onChange={(e) => handleFilterChange({ ...filters, productInterest: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Todos los productos</option>
            <option value="Desarrollo Web">Desarrollo Web</option>
            <option value="Desarrollo IA">Desarrollo IA</option>
            <option value="Branding y Marketing">Branding y Marketing</option>
            <option value="E-commerce">E-commerce</option>
          </select>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                  Empresa
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fuente
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                  Calificación
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden xl:table-cell">
                  Puntuación
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden 2xl:table-cell">
                  Producto de Interés
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden xl:table-cell">
                  Responsable
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden 2xl:table-cell">
                  Creado
                </th>
                <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLeads.map((lead) => (
                <tr
                  key={lead.id}
                  className={`hover:bg-gray-50 transition-colors ${lead.status === LEAD_STATUS.DESCARTADO ? 'bg-red-50' : ''}`}
                  data-lead-id={lead.id}
                >
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8 lg:h-10 lg:w-10">
                        <div className={`h-8 w-8 lg:h-10 lg:w-10 rounded-full flex items-center justify-center ${
                          lead.status === LEAD_STATUS.DESCARTADO ? 'bg-red-100' : 'bg-blue-100'
                        }`}>
                          {lead.status === LEAD_STATUS.DESCARTADO ? (
                            <AlertCircle className="h-4 w-4 lg:h-5 lg:w-5 text-red-600" />
                          ) : (
                            <User className="h-4 w-4 lg:h-5 lg:w-5 text-blue-600" />
                          )}
                        </div>
                      </div>
                      <div className="ml-3 lg:ml-4">
                        <div className="flex items-center space-x-2">
                          <div className="text-sm font-medium text-gray-900">{lead.fullName}</div>
                          {lead.status === LEAD_STATUS.DESCARTADO && (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800 border border-red-300">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Descartado
                            </span>
                          )}
                        </div>
                        <div className="text-xs lg:text-sm text-gray-500 flex items-center space-x-2">
                          <Mail className="w-3 h-3" />
                          <span className="truncate max-w-[150px] lg:max-w-none">{lead.email}</span>
                        </div>
                        {lead.phone && (
                          <div className="text-xs lg:text-sm text-gray-500 flex items-center space-x-2">
                            <Phone className="w-3 h-3" />
                            <span>{lead.phone}</span>
                          </div>
                        )}
                        <div className="lg:hidden mt-1">
                          <div className="text-xs text-gray-500">{lead.company}</div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                    <div className="flex items-center">
                      <Building2 className="w-4 h-4 text-gray-400 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{lead.company}</div>
                        {lead.role && (
                          <div className="text-sm text-gray-500">{lead.role}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <LeadSourceBadge source={getLeadRealSource(lead)} size="sm" />
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                    <LeadQualifiedBadge isQualified={parseLeadFormData(lead).isQualified} size="sm" />
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors[lead.status]}`}>
                      {statusLabels[lead.status]}
                    </span>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap hidden xl:table-cell">
                    <div className="flex items-center space-x-2">
                      {lead.leadTier && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                          lead.leadTier === 'HOT'
                            ? 'bg-red-100 text-red-700'
                            : lead.leadTier === 'WARM'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {lead.leadTier}
                        </span>
                      )}
                      <div className={`flex items-center space-x-1 px-2 py-1 rounded-full ${getScoreColor(lead.score)}`}>
                        <Star className="w-3 h-3" />
                        <span className="text-xs font-medium">{lead.score}</span>
                      </div>
                      {lead.analysisStatus === 'pending' && (
                        <span className="w-2 h-2 rounded-full bg-gray-400 flex-shrink-0" title="Análisis pendiente" />
                      )}
                      {lead.analysisStatus === 'completed' && !lead.leadTier && (
                        <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" title="Análisis completado" />
                      )}
                      {lead.analysisStatus === 'failed' && (
                        <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" title="Análisis fallido" />
                      )}
                    </div>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden 2xl:table-cell">
                    <div className="max-w-xs">
                      {(() => {
                        try {
                          const interests = JSON.parse(lead.productInterest);
                          return Array.isArray(interests) ? interests.join(', ') : lead.productInterest;
                        } catch {
                          return lead.productInterest;
                        }
                      })()}
                    </div>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden xl:table-cell">
                    {lead.owner}
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden 2xl:table-cell">
                    {lead.createdAt.toLocaleDateString()}
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white">
                    <div className="flex items-center justify-end space-x-2">
                      {lead.status === LEAD_STATUS.DESCARTADO ? (
                        <>
                          <button
                            onClick={() => handleRestoreLead(lead.id, LEAD_STATUS.NUEVO)}
                            className="text-green-600 hover:text-green-900 p-1 rounded flex items-center space-x-1"
                            title="Restaurar lead"
                          >
                            <RotateCcw className="w-4 h-4" />
                            <span className="text-xs">Restaurar</span>
                          </button>
                          <button
                            onClick={() => {
                              setEditingLead(lead);
                              setIsModalOpen(true);
                            }}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded"
                            title="Editar lead"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleShowAI(lead)}
                            className="text-purple-600 hover:text-purple-900 p-1 rounded"
                            title="Análisis IA"
                          >
                            <Brain className="w-4 h-4" />
                          </button>
                          {lead.status !== LEAD_STATUS.CONVERTIDO && (
                            <>
                              <button
                                onClick={() => handleConvertLead(lead)}
                                className="text-green-600 hover:text-green-900 p-1 rounded"
                                title="Convertir a cliente"
                              >
                                <UserCheck className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setLeadDisposition({ lead, action: 'archive' })}
                                className="text-orange-600 hover:text-orange-900 p-1 rounded"
                                title="Archivar lead"
                              >
                                <Archive className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => {
                              setEditingLead(lead);
                              setIsModalOpen(true);
                            }}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded"
                            title="Editar lead"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setLeadDisposition({ lead, action: 'delete' })}
                            className="text-red-600 hover:text-red-900 p-1 rounded"
                            title="Eliminar lead"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredLeads.length === 0 && (
          <div className="text-center py-12">
            <User className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay leads</h3>
            <p className="text-gray-500 mb-6">
              {searchTerm || Object.values(filters).some(f => f) 
                ? 'No se encontraron leads con los filtros aplicados'
                : 'Comienza añadiendo tu primer lead'
              }
            </p>
            {!searchTerm && !Object.values(filters).some(f => f) && (
              <button
                onClick={() => {
                  setEditingLead(null);
                  setIsModalOpen(true);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mx-auto"
              >
                <Plus className="w-4 h-4" />
                <span>Nuevo Lead</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="mt-4 text-sm text-gray-500">
        Mostrando {filteredLeads.length} de {(leads || []).length} leads
      </div>

      {/* Lead Modal */}
      <LeadModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingLead(null);
        }}
        onSave={handleSaveLead}
        lead={editingLead}
      />

      {/* Convert Lead Modal */}
      <ConvertLeadModal
        isOpen={isConvertModalOpen}
        onClose={() => {
          setIsConvertModalOpen(false);
          setConvertingLead(null);
        }}
        onSave={handleSaveConversion}
        lead={convertingLead}
      />

      {leadDisposition && (
        <ArchiveLeadModal
          isOpen
          onClose={() => setLeadDisposition(null)}
          onConfirm={handleDispositionConfirm}
          lead={leadDisposition.lead}
          action={leadDisposition.action}
        />
      )}
      </div>

      {/* AI Analysis Panel */}
      {showAIPanel && selectedLeadForAI && (
        <div className="w-full lg:w-1/3 overflow-y-auto min-h-0 flex-shrink-0">
          <LeadAIAnalysisPanel
            lead={selectedLeadForAI}
            onClose={() => {
              setShowAIPanel(false);
              setSelectedLeadForAI(null);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default LeadsList;